import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { fpsCourseFilters } from "../filterConfigurations";
import { formatDateTime } from "../../../utils/formatDate";

const FpsCourses = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["FPS Courses"]?.edit;
  const {
    data: courses,
    loading,
    error,
  } = useFirestore("FpsCourses", "Course", "asc");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "FPS Courses";

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(courses || [], fpsCourseFilters);

  const getBooleanDisplayValue = (value) => {
    if (value === -1 || value === "-1" || value === "Yes") return "Yes";
    if (value === 0 || value === "0" || value === "No") return "No";
    if (value === null || typeof value === "undefined") return "";
    return String(value);
  };

  const columns = useMemo(
    () => [
      { field: "Course", headerName: "Course", flex: 1, minWidth: 120 },
      { field: "Subject", headerName: "Subject", flex: 1, minWidth: 120 },
      { field: "Credits", headerName: "Credits", flex: 1, minWidth: 100 },
      {
        field: "Active",
        headerName: "Active",
        flex: 1,
        minWidth: 100,
        type: "singleSelect",
        renderCell: (params) =>
          params?.value !== undefined
            ? getBooleanDisplayValue(params.value)
            : "",
        valueFormatter: (value) =>
          value !== undefined && value !== null
            ? getBooleanDisplayValue(value)
            : "",
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => formatDateTime(params.value),
      },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((course, index) => ({
      id: course.id || index, // Use course.id if available, otherwise use index
      ...course,
    }));
  }, [filteredData]);

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedCourse(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCourse(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching courses:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        <CustomFilterPanel
          filterConfig={fpsCourseFilters}
          onFiltersChange={setFilterModel}
          initialFilters={filterModel}
          collapsible={true}
          title="FPS Course Filters"
          resultCount={filteredData?.length || 0}
          totalCount={courses?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="FpsCourses"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={courses}
              templateFields={columns.map((col) => col.field)}
            />
          )}
          {canEdit && <ClearCollectionButton collectionName="FpsCourses" />}
        </Box>

        <Box sx={{ height: "70vh", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            slots={{ toolbar: SimpleToolbar }}
            slotProps={{
              toolbar: {
                fileName: "fps-courses",
              },
            }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedCourse && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleDialogClose}
          courseDetail={selectedCourse}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FpsCourses;
